import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'

import { listOrganizationUsers } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListOrganizationUsersRequestSchema, OrganizationUserType } from '@/gen/cortado/v1/organizations_pb'
import { authStore } from '@/stores/auth.store'
import { useCurrentUser } from '@/stores/user.store'
import { isDemoModeActive } from '@/lib/demo-mode'
import { ADMIN_PERMISSION_KEY } from '@/lib/role-permissions'
import { useSupportSession } from '@/hooks/use-support-session'

/**
 * Ability layer for a single organization: `can(permission)` is a plain
 * set-membership check against the current user's server-expanded
 * `effective_permissions` for `organizationId`.
 *
 * Sourced from ListOrganizationUsers, NOT GetMe (RBAC audit plan, decision
 * D7). Every role mutation (`useUpdateOrganizationUser`,
 * `useAddUserToOrganization`, `useRemoveUserFromOrganization` in
 * organization.store.ts) invalidates ListOrganizationUsers' cache, whereas
 * GetMe carries the query client's global 5-minute `staleTime` with
 * `refetchOnWindowFocus` disabled and nothing invalidates it on role change
 * (verified: no `schema: getMe` invalidation anywhere in the store layer,
 * only `setQueryData` calls from `useCreateOrganization`/`useUpdateMe`/
 * `Login.tsx`). Sourcing from GetMe would leave a demoted user's UI holding
 * elevated permissions for up to 5 minutes after the change.
 *
 * `effective_permissions` is already implication-expanded server-side
 * (`enums.ExpandPermissions`: `Admin` implies every permission, `XManage`
 * implies `XView`) — `can` does membership only, nothing more.
 *
 * Takes an explicit `organizationId` (rather than reading the globally
 * selected org) because an entity's own org can differ from the selected
 * one on a deep link or mid org-switch (see the Gmail-account comment in
 * the invoice detail route) — callers should pass the org the permission
 * actually applies to.
 */
export function usePermissions(organizationId: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const isDemo = isDemoModeActive()
  const { user } = useCurrentUser()

  const query = useQuery(
    listOrganizationUsers,
    create(ListOrganizationUsersRequestSchema, { organizationId }),
    {
      enabled: (isAuthenticated || isDemo) && !!organizationId,
      select: (data) => data?.users ?? [],
    }
  )

  const orgUsers = query.data ?? []
  const currentOrgUser = user ? orgUsers.find((orgUser) => orgUser.user?.id === user.id) : undefined
  // A Cortado superadmin in a support session has no membership row; the
  // session carries the (server-built) permission set instead. A real
  // membership always wins.
  const supportSession = useSupportSession(organizationId)
  const isSupportSession = !currentOrgUser && !!supportSession
  const permissionSet = new Set(
    currentOrgUser?.effectivePermissions ?? (isSupportSession ? supportSession.permissions : [])
  )
  // UNSPECIFIED is treated as ADMIN on input (older web/iOS clients never set
  // it), so a membership that hasn't been given an explicit type is an admin.
  const userType =
    currentOrgUser?.userType === OrganizationUserType.EVENT_STAFF
      ? OrganizationUserType.EVENT_STAFF
      : OrganizationUserType.ADMIN

  return {
    can: (permission: string) => permissionSet.has(permission),
    isAdmin: permissionSet.has(ADMIN_PERMISSION_KEY),
    userType,
    isEventStaff: userType === OrganizationUserType.EVENT_STAFF,
    isSupportSession,
    // `can` fails closed while the set is still in flight, so callers that
    // would otherwise *assert* a denial (ActionGate's tooltip) need to know
    // the difference between "denied" and "not known yet".
    isLoading: query.isLoading,
  }
}
